/**
 * ProviderSoapReview — TV-P7-09 (2026-06-02)
 *
 * Provider-facing review surface for the AI-drafted SOAP note that
 * TV-P7-08's `generateSoapNote` Cloud Function produced.
 *
 * Flow:
 *   1. Provider clicks End Visit in LiveVisit. Encounter flips to
 *      status=finished (TV-P1-04). Caller fires generateSoapNote
 *      Cloud Function — that POSTs a DocumentReference with
 *      docStatus='preliminary' to FHIR.
 *   2. Provider lands on /sign/:encounterId (this component).
 *   3. We fetch the latest preliminary DocumentReference for this
 *      Encounter, extract the structured SOAP JSON from the
 *      ai-soap extension, render each section as an editable
 *      textarea.
 *   4. Provider edits inline, clicks "Sign & finalize". We PUT
 *      the DocumentReference back with docStatus='final' + the
 *      edited content + authenticator = provider's Practitioner ref.
 *   5. After success, navigate to /summary/:encounterId (the
 *      patient-facing AVS).
 *
 * Edge case: if the SOAP-gen hasn't finished yet (Gemini takes
 * 5-15 s on a busy day) we poll the FHIR Encounter for the latest
 * DocumentReference every 2 s with a 60 s ceiling. Surface a
 * "still drafting…" state until it lands or times out.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CheckCircle2, Loader2, AlertTriangle, RefreshCw, Plus, Minus } from 'lucide-react';
import { Card, Badge } from '@/components';
import { useFHIR } from '@/fhir/hooks';

const POLL_INTERVAL_MS = 2_000;
const POLL_TIMEOUT_MS = 60_000;

function decodeAttachment(attachment) {
  if (!attachment?.data) return null;
  try {
    // Browser btoa/atob aren't UTF-8 safe; use TextDecoder for the
    // emoji-free, but still potentially-multilingual SOAP text.
    const bytes = Uint8Array.from(atob(attachment.data), (c) => c.charCodeAt(0));
    return new TextDecoder('utf-8').decode(bytes);
  } catch {
    return null;
  }
}

function extractSoap(documentReference) {
  if (!documentReference) return null;
  const ext = (documentReference.extension || []).find(
    (e) => e.url === 'https://collabkare.io/fhir/StructureDefinition/ai-soap',
  );
  const decoded = decodeAttachment(ext?.valueAttachment);
  if (!decoded) return null;
  try {
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

function renderSoapAsText(soap) {
  return [
    'SUBJECTIVE',
    soap.subjective || '(not documented)',
    '',
    'OBJECTIVE',
    soap.objective || '(not documented)',
    '',
    'ASSESSMENT',
    ...(soap.assessment.length
      ? soap.assessment.map((a, i) => `${i + 1}. ${a}`)
      : ['(not documented)']),
    '',
    'PLAN',
    ...(soap.plan.length
      ? soap.plan.map((p, i) => `${i + 1}. ${p}`)
      : ['(not documented)']),
    '',
    'FOLLOW-UP',
    soap.followUp || '(not documented)',
    ...(soap.redFlags ? ['', 'RED FLAGS', soap.redFlags] : []),
  ].join('\n');
}

export default function ProviderSoapReview() {
  const { encounterId } = useParams();
  const navigate = useNavigate();
  const fhir = useFHIR();

  const [documentReference, setDocumentReference] = useState(null);
  const [soap, setSoap] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [polling, setPolling] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState(null);

  // Fetch + poll until a preliminary DocumentReference appears.
  useEffect(() => {
    if (!encounterId || !fhir) return undefined;

    let cancelled = false;
    let intervalId = null;
    const startedAt = Date.now();

    async function fetchLatest() {
      try {
        const bundle = await fhir.search('DocumentReference', {
          encounter: `Encounter/${encounterId}`,
          _sort: '-date',
          _count: 1,
        });
        if (cancelled) return;
        const entry = bundle?.entry?.[0]?.resource;
        if (entry) {
          setDocumentReference(entry);
          const parsed = extractSoap(entry);
          setSoap(parsed);
          setPolling(false);
          if (intervalId) clearInterval(intervalId);
          return;
        }
        // Nothing yet — keep polling unless we've timed out.
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
          setPolling(false);
          setLoadError(
            'AI-drafted note hasn\'t arrived after 60 s. ' +
              'The Cloud Function may have failed — check function logs ' +
              'and use "Retry" once it\'s healthy.',
          );
          if (intervalId) clearInterval(intervalId);
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(err.message);
          setPolling(false);
          if (intervalId) clearInterval(intervalId);
        }
      }
    }

    fetchLatest();
    intervalId = setInterval(fetchLatest, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [encounterId, fhir]);

  const isPreliminary = documentReference?.docStatus === 'preliminary';
  const isFinal = documentReference?.docStatus === 'final';

  const handleSign = async () => {
    if (!soap || !documentReference) return;
    setSigning(true);
    setSignError(null);
    try {
      const noteText = renderSoapAsText(soap);
      const noteB64 = btoa(unescape(encodeURIComponent(noteText)));

      const soapB64 = btoa(
        unescape(encodeURIComponent(JSON.stringify(soap))),
      );

      const updated = {
        ...documentReference,
        docStatus: 'final',
        // Provider-signed timestamp. Keeps the original `date` field
        // (when the AI drafted it) intact for audit.
        authenticator: documentReference.author?.[0]
          ? { reference: documentReference.author[0].reference }
          : undefined,
        content: [
          {
            attachment: {
              contentType: 'text/plain',
              data: noteB64,
              title: documentReference.content?.[0]?.attachment?.title
                || 'AI-drafted progress note (provider-signed)',
            },
          },
        ],
        extension: (documentReference.extension || [])
          .filter(
            (e) =>
              e.url !==
              'https://collabkare.io/fhir/StructureDefinition/ai-soap',
          )
          .concat([
            {
              url: 'https://collabkare.io/fhir/StructureDefinition/ai-soap',
              valueAttachment: {
                contentType: 'application/json',
                data: soapB64,
                title: 'Structured SOAP JSON (provider-edited)',
              },
            },
            {
              url: 'https://collabkare.io/fhir/StructureDefinition/ai-signed-at',
              valueDateTime: new Date().toISOString(),
            },
          ]),
      };

      await fhir.put(`DocumentReference/${documentReference.id}`, updated);
      navigate(`/summary/${encounterId}`);
    } catch (err) {
      setSignError(err.message);
    } finally {
      setSigning(false);
    }
  };

  // Tiny inline editors. Plain textareas keep the markup minimal +
  // dodge a rich-text dependency. The SOAP text is short clinical
  // prose so the experience is fine.
  const updateField = (field, value) =>
    setSoap((s) => ({ ...s, [field]: value }));

  const updateArrayItem = (field, index, value) =>
    setSoap((s) => ({
      ...s,
      [field]: s[field].map((item, i) => (i === index ? value : item)),
    }));

  const addArrayItem = (field) =>
    setSoap((s) => ({ ...s, [field]: [...(s[field] || []), ''] }));

  const removeArrayItem = (field, index) =>
    setSoap((s) => ({
      ...s,
      [field]: s[field].filter((_, i) => i !== index),
    }));

  // ── Render ────────────────────────────────────────────────────

  if (polling && !soap) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <Loader2 className="w-8 h-8 text-blue-400 animate-spin mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-white mb-2">
            Drafting your SOAP note…
          </h2>
          <p className="text-sm text-slate-400">
            Gemini is reviewing the transcript. This usually takes 5–15 seconds.
          </p>
        </Card>
      </div>
    );
  }

  if (loadError && !soap) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <AlertTriangle className="w-8 h-8 text-amber-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-white mb-2">Couldn't load the draft</h2>
          <p className="text-sm text-slate-400 mb-4">{loadError}</p>
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg"
          >
            <RefreshCw className="w-4 h-4" /> Retry
          </button>
        </Card>
      </div>
    );
  }

  if (!soap) {
    return null;
  }

  if (isFinal) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <CheckCircle2 className="w-8 h-8 text-green-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-white mb-2">Already signed</h2>
          <p className="text-sm text-slate-400 mb-4">
            This note was finalised earlier. No further action needed.
          </p>
          <button
            onClick={() => navigate(`/summary/${encounterId}`)}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg"
          >
            Go to patient summary
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 p-6">
      <div className="max-w-3xl mx-auto space-y-4">
        <header className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">SOAP note review</h1>
            <p className="text-sm text-slate-400 mt-1">
              AI-drafted from the visit transcript. Edit as needed, then sign
              to finalize.
            </p>
          </div>
          <Badge className="bg-amber-500/10 text-amber-300 border border-amber-500/30">
            {isPreliminary ? 'Preliminary' : documentReference.docStatus}
          </Badge>
        </header>

        {typeof soap.confidence === 'number' && (
          <Card className="p-3 bg-slate-900/60">
            <p className="text-xs text-slate-400">
              Model self-confidence:{' '}
              <span className="text-slate-200 font-mono">
                {Math.round(soap.confidence * 100)}%
              </span>
              {soap.confidence < 0.6 && (
                <span className="ml-2 text-amber-400">
                  Low — review carefully before signing.
                </span>
              )}
            </p>
          </Card>
        )}

        {/* Subjective */}
        <SoapSection title="Subjective" tag="S" tone="blue">
          <textarea
            value={soap.subjective}
            onChange={(e) => updateField('subjective', e.target.value)}
            rows={4}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-sm text-slate-100"
          />
        </SoapSection>

        {/* Objective */}
        <SoapSection title="Objective" tag="O" tone="green">
          <textarea
            value={soap.objective}
            onChange={(e) => updateField('objective', e.target.value)}
            rows={4}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-sm text-slate-100"
          />
        </SoapSection>

        {/* Assessment (array) */}
        <SoapSection title="Assessment" tag="A" tone="amber">
          <ArrayEditor
            items={soap.assessment}
            onChange={(i, v) => updateArrayItem('assessment', i, v)}
            onAdd={() => addArrayItem('assessment')}
            onRemove={(i) => removeArrayItem('assessment', i)}
            placeholder="Diagnosis or active problem…"
          />
        </SoapSection>

        {/* Plan (array) */}
        <SoapSection title="Plan" tag="P" tone="purple">
          <ArrayEditor
            items={soap.plan}
            onChange={(i, v) => updateArrayItem('plan', i, v)}
            onAdd={() => addArrayItem('plan')}
            onRemove={(i) => removeArrayItem('plan', i)}
            placeholder="Action item…"
          />
        </SoapSection>

        {/* Follow-up */}
        <SoapSection title="Follow-up" tag="F" tone="slate">
          <input
            type="text"
            value={soap.followUp}
            onChange={(e) => updateField('followUp', e.target.value)}
            placeholder="e.g. 2 weeks, telehealth"
            className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-sm text-slate-100"
          />
        </SoapSection>

        {/* Red flags */}
        {(soap.redFlags || true) && (
          <SoapSection title="Red flags" tag="!" tone="red">
            <textarea
              value={soap.redFlags}
              onChange={(e) => updateField('redFlags', e.target.value)}
              rows={2}
              placeholder="Symptoms / findings worth escalating. Empty if none."
              className="w-full bg-slate-900 border border-slate-700 rounded-lg p-3 text-sm text-slate-100"
            />
          </SoapSection>
        )}

        {signError && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-300">
            Failed to sign: {signError}
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            onClick={() => navigate(`/summary/${encounterId}`)}
            disabled={signing}
            className="px-4 py-2 text-sm text-slate-300 hover:text-white disabled:opacity-50"
          >
            Skip for now
          </button>
          <button
            onClick={handleSign}
            disabled={signing}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold rounded-lg"
          >
            {signing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4" />
            )}
            {signing ? 'Signing…' : 'Sign & finalize'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Helper components ─────────────────────────────────────────────────────

function SoapSection({ title, tag, tone, children }) {
  const toneClasses = {
    blue: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
    green: 'bg-green-500/15 text-green-400 border-green-500/30',
    amber: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
    purple: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
    slate: 'bg-slate-700/40 text-slate-300 border-slate-600/40',
    red: 'bg-red-500/15 text-red-400 border-red-500/30',
  };
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <span
          className={`inline-flex items-center justify-center w-7 h-7 rounded-md text-xs font-bold border ${toneClasses[tone] || toneClasses.slate}`}
        >
          {tag}
        </span>
        <h2 className="text-base font-semibold text-white">{title}</h2>
      </div>
      {children}
    </Card>
  );
}

function ArrayEditor({ items, onChange, onAdd, onRemove, placeholder }) {
  return (
    <div className="space-y-2">
      {(items || []).map((item, idx) => (
        <div key={idx} className="flex gap-2">
          <input
            type="text"
            value={item}
            onChange={(e) => onChange(idx, e.target.value)}
            placeholder={placeholder}
            className="flex-1 bg-slate-900 border border-slate-700 rounded-lg p-2.5 text-sm text-slate-100"
          />
          <button
            onClick={() => onRemove(idx)}
            className="px-2 text-slate-500 hover:text-red-400"
            title="Remove"
          >
            <Minus className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button
        onClick={onAdd}
        className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300"
      >
        <Plus className="w-3 h-3" /> Add item
      </button>
    </div>
  );
}
